import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { Calendar, MapPin, Lock } from 'lucide-react';
import { API } from '@/App';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

/**
 * Lists either the events a user has hosted, or the past events they attended.
 *
 * - `hosted` is always public (anyone can view the list)
 * - `attended` is restricted to mutual connections (server-enforced). If the
 *   viewer is not a mutual connection, the backend returns `restricted: true`
 *   with an empty events array, and we render a privacy explainer instead.
 */
const EventsListDialog = ({ open, onOpenChange, type, userId, userName, isOwnProfile }) => {
  // Single state object so we update once per fetch lifecycle (avoids the
  // react-hooks/set-state-in-effect lint warning that fires on multiple
  // synchronous setStates inside useEffect).
  const [state, setState] = useState({
    events: [],
    count: 0,
    restricted: false,
    loading: false
  });
  const { events, count, restricted, loading } = state;

  useEffect(() => {
    if (!open || !type || !userId) return;
    let cancelled = false;
    Promise.resolve().then(() =>
      setState({ events: [], count: 0, restricted: false, loading: true })
    );
    axios.get(`${API}/users/${userId}/events/${type}`)
      .then((res) => {
        if (cancelled) return;
        setState({
          events: res.data.events || [],
          count: res.data.count || 0,
          restricted: !!res.data.restricted,
          loading: false
        });
      })
      .catch(() => {
        if (!cancelled) setState((s) => ({ ...s, loading: false, events: [] }));
      });
    return () => { cancelled = true; };
  }, [open, type, userId]);

  const title = type === 'hosted'
    ? `Events hosted by ${userName || 'this user'}`
    : `Events attended by ${userName || 'this user'}`;

  const emptyText = type === 'hosted'
    ? "Hasn't hosted any events yet."
    : "No past events to show yet.";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="events-list-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {count} {count === 1 ? 'event' : 'events'}
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="py-10 text-center text-sm text-muted-foreground">Loading…</div>
        )}

        {!loading && restricted && (
          <div className="py-8 px-2 text-center space-y-4" data-testid="events-list-restricted">
            <div className="w-12 h-12 mx-auto rounded-full bg-muted flex items-center justify-center">
              <Lock className="w-6 h-6 text-muted-foreground" />
            </div>
            <div>
              <p className="text-sm font-medium">Only mutual connections can see this list</p>
              <p className="text-xs text-muted-foreground mt-1">
                {userName || 'This user'} has been to <strong>{count}</strong>{' '}
                {count === 1 ? 'event' : 'events'}. Connect with them to see which ones.
              </p>
            </div>
          </div>
        )}

        {!loading && !restricted && events.length === 0 && (
          <div className="py-8 text-center text-sm text-muted-foreground">
            {isOwnProfile ? (type === 'hosted'
              ? "You haven't hosted any events yet. Tap the + button to create your first one!"
              : "You haven't attended any past events yet. Browse events to find your next one."
            ) : emptyText}
          </div>
        )}

        {!loading && !restricted && events.length > 0 && (
          <ul className="space-y-2" data-testid="events-list">
            {events.map((ev) => (
              <li key={ev.event_id || ev.id}>
                <Link
                  to={`/events/${ev.event_id || ev.id}`}
                  onClick={() => onOpenChange(false)}
                  className="block p-3 rounded-xl border border-border hover:border-primary/40 hover:bg-primary/5 transition-colors"
                  data-testid={`events-list-item-${ev.event_id || ev.id}`}
                >
                  <div className="font-medium leading-tight">{ev.title}</div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground mt-1.5">
                    {ev.event_date && (
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {(() => {
                          try { return format(new Date(ev.event_date), 'MMM d, yyyy'); }
                          catch { return ev.event_date; }
                        })()}
                      </span>
                    )}
                    {ev.city && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {ev.city}{ev.country ? `, ${ev.country}` : ''}
                      </span>
                    )}
                    {typeof ev.attendees_count === 'number' && (
                      <span>· {ev.attendees_count} going</span>
                    )}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}

        <div className="pt-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="w-full rounded-full"
            data-testid="events-list-close"
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EventsListDialog;
